import { buildCssVars, type ThemeConfig } from "@reducate/theme";
import {
  peerWhitelabelThemeSchema,
  peerStartThemeSchema,
  type BuiltInThemeId,
  type PeerWhitelabelTheme,
  type PeerStartTheme,
} from "@peer-start/platform-contracts";

export {
  peerWhitelabelThemeSchema,
  peerStartThemeSchema,
  type BuiltInThemeId,
  type PeerWhitelabelTheme,
  type PeerStartTheme,
};

// Text colours from the design palette. Brand fills stay on the colour tokens.
const text = {
  primary: "#27272A",
  secondary: "#52525B",
  muted: "#72727E",
  inverse: "#FFFFFF",
  disabled: "#A1A1AA",
  link: "#0076B8",
} as const;

// Matches peer-ux `ewiseTheme` (packages/theme/src/defaults.ts), without the unused purple token.
export const peerStartTheme: PeerStartTheme = {
  id: "peer-start",
  name: "Peer Start",
  colors: {
    background: "#ffffff",
    foreground: text.primary,
    card: { DEFAULT: "#FFFFFF", foreground: text.primary },
    popover: { DEFAULT: "#ffffff", foreground: text.primary },
    primary: { DEFAULT: "#842760", foreground: text.inverse },
    secondary: { DEFAULT: "#ffffff", foreground: text.primary },
    muted: { DEFAULT: "#ECECEE", foreground: text.muted },
    accent: { DEFAULT: "#EBF8FF", foreground: "#00273D" },
    destructive: { DEFAULT: "#F8D3D3", foreground: "#6A0606", solid: "#CC0B0B" },
    border: "#ECECEE",
    input: "#ECECEE",
    ring: "#842760",
    chart: { "1": "#842760", "2": "#EA850C", "3": "#00A4FF", "4": "#62A000", "5": "#983BF6" },
    sidebar: {
      DEFAULT: "#842760",
      foreground: text.inverse,
      primary: { DEFAULT: "#842760", foreground: text.inverse },
      accent: { DEFAULT: "#F3E8ED", foreground: "#360724" },
      border: "#F6F6F6",
      ring: "#842760",
    },
    success: { DEFAULT: "#EBFFCC", foreground: "#385C00" },
    warning: { DEFAULT: "#FFE7CE", foreground: "#6B3D05" },
    info: { DEFAULT: "#DBF2FF", foreground: "#005C8F" },
  },
  radius: { DEFAULT: "0.75rem" },
  shadows: {
    color: "rgb(123 31 110)",
    opacity: 0.1,
    "2xs": "0px 1px 2px 0px rgb(123 31 110 / 0.05)",
    xs: "0px 1px 2px 0px rgb(123 31 110 / 0.05)",
    sm: "0px 1px 3px 0px rgb(123 31 110 / 0.1), 0px 1px 2px -1px rgb(123 31 110 / 0.1)",
    DEFAULT: "0px 4px 6px -1px rgb(123 31 110 / 0.1), 0px 2px 4px -2px rgb(123 31 110 / 0.1)",
    md: "0px 4px 6px -1px rgb(123 31 110 / 0.1), 0px 2px 4px -2px rgb(123 31 110 / 0.1)",
    lg: "0px 10px 15px -3px rgb(123 31 110 / 0.1), 0px 4px 6px -4px rgb(123 31 110 / 0.1)",
    xl: "0px 20px 25px -5px rgb(123 31 110 / 0.1), 0px 8px 10px -6px rgb(123 31 110 / 0.1)",
    "2xl": "0px 25px 50px -12px rgb(123 31 110 / 0.25)",
  },
  typography: {
    fontSans: "'Roboto', system-ui, sans-serif",
    fontSerif: "Georgia, serif",
    fontMono: "'JetBrains Mono', monospace",
    baseFontSize: "15px",
    lineHeight: "1.2",
  },
};

export const peerStartWarmTheme: PeerStartTheme = {
  ...peerStartTheme,
  id: "peer-start-warm",
  name: "Warm",
  colors: {
    ...peerStartTheme.colors,
    background: "#fffdf9",
    card: { DEFAULT: "#fffdf9", foreground: text.primary },
    popover: { DEFAULT: "#fffdf9", foreground: text.primary },
    foreground: text.primary,
    primary: { DEFAULT: "#56354f", foreground: text.inverse },
    secondary: { DEFAULT: "#fffdf9", foreground: text.primary },
    accent: { DEFAULT: "#f7e8ee", foreground: "#56354f" },
    muted: { DEFAULT: "#eee4e8", foreground: text.muted },
    border: "#ded0d6",
    input: "#ded0d6",
    ring: "#56354f",
    sidebar: {
      DEFAULT: "#56354f",
      foreground: text.inverse,
      primary: { DEFAULT: "#56354f", foreground: text.inverse },
      accent: { DEFAULT: "#eee4e8", foreground: "#3f2238" },
      border: "#f8f1e8",
      ring: "#56354f",
    },
  },
  radius: { DEFAULT: "0.75rem" },
  shadows: {
    color: "rgb(86 53 79)",
    opacity: 0.1,
    "2xs": "0 1px 2px 0 rgb(54 7 36 / 0.05)",
    xs: "0 1px 2px 0 rgb(54 7 36 / 0.05)",
    sm: "0 1px 3px 0 rgb(54 7 36 / 0.08), 0 1px 2px -1px rgb(54 7 36 / 0.08)",
    DEFAULT: "0 4px 8px -2px rgb(54 7 36 / 0.10), 0 2px 4px -2px rgb(54 7 36 / 0.08)",
    md: "0 4px 8px -2px rgb(54 7 36 / 0.10), 0 2px 4px -2px rgb(54 7 36 / 0.08)",
    lg: "0 12px 24px -6px rgb(54 7 36 / 0.14), 0 4px 8px -4px rgb(54 7 36 / 0.10)",
    xl: "0 20px 40px -5px rgb(54 7 36 / 0.18)",
    "2xl": "0 25px 50px -12px rgb(54 7 36 / 0.25)",
  },
  typography: {
    ...peerStartTheme.typography,
    fontSans: 'Roboto, "Segoe UI", Arial, sans-serif',
    baseFontSize: "15px",
    lineHeight: "1.35",
  },
};

export const themes = { "peer-start": peerStartTheme, "peer-start-warm": peerStartWarmTheme } as const;

export function buildThemeVariables(theme: PeerStartTheme): Record<string, string> {
  // Retain the established readable text palette while sharing Reducate's token engine.
  const config: ThemeConfig = {
    ...theme,
    colors: {
      ...theme.colors,
      foreground: text.primary,
      card: { ...theme.colors.card, foreground: text.primary },
      popover: { ...theme.colors.popover, foreground: text.primary },
      primary: { ...theme.colors.primary, foreground: text.inverse },
      secondary: { ...theme.colors.secondary, foreground: text.primary },
      muted: { ...theme.colors.muted, foreground: text.muted },
      sidebar: {
        ...theme.colors.sidebar,
        foreground: text.inverse,
        primary: { ...theme.colors.sidebar.primary, foreground: text.inverse },
      },
    },
  };
  const variables: Record<string, string> = {
    ...buildCssVars(config),
    "--primary-deep": "#5A0C3C",
    "--elevation-xs": theme.shadows.xs,
    "--elevation-sm": theme.shadows.sm,
    "--elevation-md": theme.shadows.md,
    "--elevation-lg": theme.shadows.lg,
    "--text-primary": text.primary,
    "--text-secondary": text.secondary,
    "--text-muted": text.muted,
    "--text-inverse": text.inverse,
    "--text-disabled": text.disabled,
    "--text-link": text.link,
    "--font-family-sans": theme.typography.fontSans,
    "--font-weight-regular": "400",
    "--font-weight-semibold": "600",
    "--font-size-base": theme.typography.baseFontSize ?? "15px",
    "--line-height": theme.typography.lineHeight ?? "1.35",
    "--base-font-size": theme.typography.baseFontSize ?? "15px",
    "--base-line-height": theme.typography.lineHeight ?? "1.35",
  };
  for (const component of ["button", "input", "card", "dialog"] as const) {
    variables[`--radius-${component}`] = theme.radius[component] ?? theme.radius.DEFAULT;
  }
  return variables;
}

export function applyThemeConfig(theme: PeerStartTheme, root: HTMLElement = document.documentElement) {
  for (const [property, value] of Object.entries(buildThemeVariables(theme))) {
    root.style.setProperty(property, value);
  }
}

export function applyTheme(themeId: BuiltInThemeId, root: HTMLElement = document.documentElement) {
  applyThemeConfig(themes[themeId], root);
}
